import type {
  CustomColumnDefinition,
  CustomFieldDefinition,
} from "@/app/api/types/featureFlags";
import type { ColumnDef } from "./types";

/**
 * Generates the dynamic, flag-defined custom value columns shared by
 * `/recap`, `/purchases` (via `useRecapColumns`) and `/recap_transport` (via
 * `useTransportColumns`).
 *
 * Custom columns are distinct from custom *fields*: fields collapse into one
 * popover cell (`CustomFieldsCell`), whereas each custom column is its own
 * inline numeric column — editable per row like `quantity`/`unit`, summed in
 * the footer. The value lives on `flow_metadata[key]`. `FlowRow` routes any
 * column carrying `customColumnKey` to `CustomColumnCell`; the rest of the
 * recap/transport table chain is untouched.
 */

/** Prefix for the generated column id, keeps it unique + recognizable. */
export const CUSTOM_COLUMN_ID_PREFIX = "custom_col:";

/**
 * Keeps only the definitions that apply to `mode`. A def without `modes`
 * applies everywhere (legacy configs predate scoping). `mode: null`
 * (transport workspace, surfaces outside a RecapProvider) skips scoping —
 * sales/purchases scoping is meaningless there.
 */
export function filterDefsByMode<
  T extends { modes?: ("sales" | "purchases")[] },
>(defs: T[], mode: "sales" | "purchases" | null): T[] {
  if (!mode) return defs;
  return defs.filter((def) => !def.modes || def.modes.includes(mode));
}

const DEFAULT_CUSTOM_COLUMN_WIDTH_PX = 70;

/** True when a feature-flag value is a non-empty custom-column definition list. */
export function isCustomColumnList(
  value: unknown,
): value is CustomColumnDefinition[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (v) =>
        typeof v === "object" &&
        v !== null &&
        typeof (v as CustomColumnDefinition).key === "string" &&
        typeof (v as CustomColumnDefinition).label === "string",
    )
  );
}

/**
 * Maps each `CustomColumnDefinition` to a `ColumnDef`. The id is
 * `custom_col:<key>` (unique for React keys + width tracking); `headerLabel`
 * carries the literal label; `customColumnKey` carries the `flow_metadata`
 * key. `modes` mirrors the def's scoping (default both recap modes) — the
 * transport resolver appends these too and casts, so a single generator
 * serves all surfaces. Callers pre-filter with `filterDefsByMode`.
 */
export function buildCustomColumns(
  defs: CustomColumnDefinition[],
): ColumnDef[] {
  return defs.map((def) => ({
    id: `${CUSTOM_COLUMN_ID_PREFIX}${def.key}`,
    headerKey: "",
    headerLabel: def.label,
    customColumnKey: def.key,
    width: `${def.width ?? DEFAULT_CUSTOM_COLUMN_WIDTH_PX}px`,
    modes: def.modes ?? ["sales", "purchases"],
    align: "left",
  }));
}

/** Prefix for inline custom field column ids. */
export const CUSTOM_FIELD_ID_PREFIX = "custom_field:";

/** Default widths per field type when no explicit width is given. */
const FIELD_TYPE_WIDTH: Record<string, number> = {
  select: 120,
  text: 100,
  number: 70,
  checkbox: 50,
  // A native date input needs room for dd/mm/yyyy plus the picker glyph.
  date: 130,
};

/**
 * Maps each `CustomFieldDefinition` to a `ColumnDef` with `customFieldDef`
 * so `FlowRow` routes to `InlineCustomFieldCell`. Replaces the single
 * `custom_fields` popover column when inline display is preferred.
 */
export function buildInlineCustomFieldColumns(
  defs: CustomFieldDefinition[],
): ColumnDef[] {
  return defs
    .filter((def) => def.type in FIELD_TYPE_WIDTH)
    .map((def) => ({
      id: `${CUSTOM_FIELD_ID_PREFIX}${def.key}`,
      headerKey: "",
      headerLabel: def.label,
      customFieldDef: def,
      width: `${FIELD_TYPE_WIDTH[def.type]}px`,
      modes: def.modes ?? ["sales", "purchases"],
      align: "left",
    }));
}

/** Trailing control columns custom value columns should sit *before*. */
const CONTROL_COLUMN_IDS: ReadonlySet<string> = new Set(["status", "actions"]);

/**
 * Anchor for custom VALUE columns (Palettes, Rolls…): they are quantities, so
 * they read best grouped with quantity/unit/weight rather than stranded at the
 * tail. Splicing before `price` lands them directly after the weight column.
 * Falls back to `total` then the control columns when a company hides `price`.
 */
export const QUANTITY_ANCHOR_IDS: ReadonlySet<string> = new Set([
  "price",
  "total",
  "status",
  "actions",
]);

/**
 * Splices `custom` into `base` just before the first column whose id is in
 * `anchorIds` so the value columns land at a deliberate position. Defaults to
 * the trailing control columns (`status`/`actions`) — value columns group with
 * the data columns and the status dot + delete button stay rightmost (recap
 * behavior, unchanged). Transport passes its own anchor to slot custom columns
 * right after quantity/weight instead of at the tail. Falls back to appending
 * when no anchor column is present. Generic over the column shape so both the
 * recap (`ColumnDef`) and transport (`TransportColumnDef`) resolvers reuse it.
 */
export function withCustomColumns<T extends { id: string }>(
  base: T[],
  custom: T[],
  anchorIds: ReadonlySet<string> = CONTROL_COLUMN_IDS,
): T[] {
  if (custom.length === 0) return base;
  const idx = base.findIndex((c) => anchorIds.has(c.id));
  if (idx === -1) return [...base, ...custom];
  return [...base.slice(0, idx), ...custom, ...base.slice(idx)];
}

/**
 * Total px width of a column set. `width` is a CSS string ("234px"); anything
 * unparseable falls back to `fallbackPx` so one odd column can't zero the sum
 * and wrongly convince the caller everything fits.
 */
export function sumColumnWidths<T extends { width?: string }>(
  cols: T[],
  fallbackPx = 80,
): number {
  return cols.reduce((total, col) => {
    const parsed = parseFloat(col.width ?? "");
    return total + (Number.isFinite(parsed) ? parsed : fallbackPx);
  }, 0);
}

/**
 * Horizontal space the page chrome eats before the flows table gets any:
 * sidebar + page padding + the client-section gutter + a scrollbar. Deliberately
 * generous — overshooting costs a popover, undershooting costs a clipped table.
 */
export const TABLE_CHROME_ALLOWANCE_PX = 300;
